import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { getImageUrl } from "../../utils/imageUtils";
import { buildCustomerLoginUrl } from "../../utils/customerLink";
import { money, fmt, fmtDate, addrLine, describeTarget } from "../../utils/infinityCase";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { FileText, Clock, Mail, Download } from "lucide-react";
import PaymentStatusBadge from "../shared/PaymentStatusBadge";
import OrderStageBadge from "../shared/OrderStageBadge";
import EmailOrderDialog from "./EmailOrderDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_OPTS = ["pending", "confirmed", "processing", "shipped", "delivered", "cancelled"];
// The stage pill comes from shared/OrderStageBadge. The local stage table that used to
// live here fell back to stage 0 for an unmatched status, so a declined order read
// "Payment failed" + "Order Placed" — an order that was never placed.

/**
 * Case-centric Infinity Touch order detail. A memorial case → many orders; this
 * shows the case + customer link, then one section per order with its status
 * timeline + inline status change. Auto-scrolls to `focusOrderId` when opened.
 */
export default function InfinityCaseDetail({ open, onClose, memorialRecordId, focusOrderId, apiBase = "admin", readOnly = false, allowEmailOrders = true }) {
  const [record, setRecord] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  // One dialog serves both modes; `order` null means "create a new email order".
  const [emailOrderDialog, setEmailOrderDialog] = useState({ open: false, order: null });
  // Site name/tagline/description/logo for the PDF's branded header — same public CMS
  // content already used for the infinitytouch.app tab title/favicon (App.js's
  // applyInfinityTouchBranding), fetched once per open rather than per download.
  const [branding, setBranding] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const sectionRefs = useRef({});

  const load = useCallback(async () => {
    if (!memorialRecordId) return;
    setLoading(true);
    try {
      const [recRes, ordRes] = await Promise.all([
        axios.get(`${API}/${apiBase}/memorial-records/${memorialRecordId}`).catch(() => ({ data: null })),
        axios.get(`${API}/${apiBase}/orders`, { params: { memorial_record_id: memorialRecordId, limit: 100 } }),
      ]);
      setRecord(recRes.data);
      // Admin orders endpoint returns { orders: [...] }; reseller returns a bare array.
      setOrders(Array.isArray(ordRes.data) ? ordRes.data : (ordRes.data?.orders || []));
    } catch (e) {
      toast.error("Failed to load case");
    } finally {
      setLoading(false);
    }
  }, [memorialRecordId, apiBase]);

  useEffect(() => { if (open) load(); }, [open, load]);

  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/website/content`).then(({ data }) => setBranding(data)).catch(() => setBranding(null));
  }, [open]);

  // Auto-scroll (1.4) to the focused order once loaded.
  useEffect(() => {
    if (!loading && focusOrderId && sectionRefs.current[focusOrderId]) {
      setTimeout(() => sectionRefs.current[focusOrderId]?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
    }
  }, [loading, focusOrderId, orders]);

  const s = record?.subject || {};
  // The PIN is case-level (every order carries a copy), but not every order necessarily
  // has one — pick the first that does so the link survives a newer order without it.
  // With no orders at all, fall back to the case's own code: the family must be able to
  // order as soon as the funeral home saves the case, which is the whole point of
  // case_code (TR-25 item 1). Order references stay preferred so links already in the
  // wild and the ones staff have copied before keep matching what is shown here.
  const linkOrder = orders.find((o) => o.customer_pin) || orders[0];
  const linkRef = linkOrder?.order_number || linkOrder?.order_id || record?.case_code;
  const linkPin = linkOrder?.customer_pin || record?.customer_pin;
  const link = buildCustomerLoginUrl(linkRef, linkPin);

  // "Email Ordering" records an order that reached us by email/phone instead of through
  // the app or the customer portal. Offered on EVERY case, including ones that already
  // carry orders — a family can phone in a second order against a case they have already
  // ordered from, and staff still need somewhere to log it. A case can therefore hold
  // SEVERAL email orders; each is moved along from its own card below, not from this
  // button, which now only ever opens a new one.
  // Admin-only: this popup is also rendered by the funeral-home panel with readOnly, and by
  // the ops Orders page with allowEmailOrders false (ops may change order status only).
  const emailOrders = orders.filter((o) => o.is_email_order);
  const canEmailOrder = !readOnly && allowEmailOrders;

  // A real PDF download (components/admin/InfinityCasePdf), loaded on first click so
  // react-pdf stays out of the main bundle.
  const handleDownloadPdf = async () => {
    setDownloading(true);
    try {
      const { downloadCasePdf } = await import("./InfinityCasePdf");
      await downloadCasePdf({ record, orders, branding, link, pin: linkPin });
    } catch (e) {
      console.error("Case PDF failed", e);
      toast.error("Failed to create the PDF");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <>
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><FileText className="w-5 h-5 text-blue-600" /> Case {record?.case_number || ""}</DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="py-12 text-center text-slate-400">Loading…</div>
        ) : (
          <div className="space-y-5">
            {/* Status + email ordering, first thing in the popup. The legacy admin put this
                button directly under the status line and that is where staff look for it;
                below the case images it sits under the fold of a scrolling modal. */}
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border bg-slate-50/60 px-4 py-3">
              <p className="text-sm text-slate-600">
                Status:{" "}
                <span className={`font-semibold ${record?.status === "ordered" ? "text-green-700" : "text-amber-600"}`}>
                  {record?.status === "ordered" ? "Print Order" : "Order Saved"}
                </span>
                {emailOrders.length > 0 && (
                  // A count, not a status. With several email orders on a case, naming
                  // one order's status here would misreport the others; each card below
                  // carries its own stage badge and timeline, which is where TR-30 1.2's
                  // "the admin must see the status change stick" is now answered.
                  <span className="ml-2 text-xs text-amber-700">
                    · {emailOrders.length} email order{emailOrders.length === 1 ? "" : "s"}
                  </span>
                )}
              </p>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" className="print:hidden" onClick={handleDownloadPdf} disabled={downloading}>
                  <Download className="w-4 h-4 mr-1.5" /> {downloading ? "Preparing…" : "Download PDF"}
                </Button>
                {canEmailOrder && (
                  <Button
                    size="sm"
                    className="print:hidden bg-green-600 hover:bg-green-700"
                    onClick={() => setEmailOrderDialog({ open: true, order: null })}
                    title="Record another order taken over email or phone against this case"
                  >
                    <Mail className="w-4 h-4 mr-1.5" /> New Email Order
                  </Button>
                )}
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              {/* Case / deceased details */}
              <Section title="Case Details">
                <div className="grid gap-x-6 gap-y-1 text-sm">
                  <KV k="Case Number" v={record?.case_number || "—"} />
                  {/* Unique, unlike case_number, which the funeral home types itself —
                      this is the reference the family login link carries. */}
                  <KV k="Case Code" v={record?.case_code || "—"} />
                  <KV k="Name" v={[s.first_name, s.middle_name, s.last_name].filter(Boolean).join(" ") || "—"} />
                  <KV k="Type" v={s.subject_type === "pet" ? "Pet" : "Human"} />
                  <KV k="Gender" v={s.gender || "—"} />
                  <KV k="Print Type" v={record?.print_type || "—"} />
                  <KV k="Date of Birth" v={fmtDate(s.date_of_birth)} />
                  <KV k="Date of Death" v={fmtDate(s.date_of_death)} />
                  <KV k="Time of Death" v={s.time_of_death || "—"} />
                  <KV k="Place of Death" v={s.place_of_death || "—"} />
                </div>
              </Section>

              {/* Funeral home (who created the case) */}
              <Section title="Funeral Home">
                {record?.funeral_home ? (
                  <div className="grid gap-x-6 gap-y-1 text-sm">
                    <KV k="Business" v={record.funeral_home.business_name || "—"} />
                    <KV k="Email" v={record.funeral_home.contact_email || "—"} />
                    <KV k="Phone" v={record.funeral_home.contact_phone || "—"} />
                    <KV k="Subdomain" v={record.funeral_home.subdomain || "—"} />
                    {record.funeral_home.business_address && (
                      <KV k="Address" v={[record.funeral_home.business_address.address, record.funeral_home.business_address.city, record.funeral_home.business_address.state, record.funeral_home.business_address.zipcode].filter(Boolean).join(", ") || "—"} />
                    )}
                  </div>
                ) : <p className="text-sm text-slate-400">—</p>}
              </Section>
            </div>

            {/* Case images — separate section */}
            <Section title="Case Images (Prints)">
              {(record?.captures || []).length === 0 ? (
                <p className="text-sm text-slate-400">No print images captured.</p>
              ) : (
                <div className="flex flex-wrap gap-3">
                  {record.captures.map((cap, i) => {
                    const url = cap.image_url ? (cap.image_url.startsWith("http") ? cap.image_url : `${process.env.REACT_APP_BACKEND_URL}${cap.image_url}`) : null;
                    return url ? (
                      <a key={i} href={url} target="_blank" rel="noopener noreferrer" download className="text-center text-[10px] text-blue-700">
                        <img src={getImageUrl(cap.image_url)} alt="" className="w-20 h-20 object-cover rounded-lg border" />
                        <div className="mt-0.5">{describeTarget(cap.target)} ↓</div>
                      </a>
                    ) : (
                      <div key={i} className="w-20 h-20 rounded-lg border bg-slate-50 flex items-center justify-center text-[10px] text-slate-300">no image</div>
                    );
                  })}
                </div>
              )}
            </Section>

            {/* Customer link */}
            <Section title="Customer Link">
              {link ? (
                <a href={link} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-700 break-all hover:underline">{link}</a>
              ) : (
                <p className="text-xs text-slate-500">—</p>
              )}
              <p className="text-xs text-slate-500 mt-1">Customer PIN: <span className="font-bold text-slate-800">{linkPin || "—"}</span></p>
            </Section>

            {/* 1.3 Orders against this case */}
            <div>
              <h4 className="font-semibold text-slate-800 mb-2">Orders ({orders.length})</h4>
              <div className="space-y-4">
                {orders.map((o) => (
                  <div key={o.order_id} ref={(el) => (sectionRefs.current[o.order_id] = el)}
                    className={`border rounded-xl p-4 ${o.order_id === focusOrderId ? "border-blue-300 ring-2 ring-blue-100" : "border-slate-200"}`}>
                    <OrderSection
                      order={o}
                      onUpdated={load}
                      apiBase={apiBase}
                      readOnly={readOnly}
                      allowEmailOrders={allowEmailOrders}
                      onUpdateEmailOrder={(order) => setEmailOrderDialog({ open: true, order })}
                    />
                  </div>
                ))}
                {orders.length === 0 && <p className="text-sm text-slate-400">No orders placed against this case yet.</p>}
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>

    {/* Sibling, not nested inside the case dialog: two Radix roots side by side layer
        and restore focus predictably. `load` refetches the case so the new/updated
        email order and its remark appear immediately. */}
    <EmailOrderDialog
      open={emailOrderDialog.open}
      // Only `open` flips on close: clearing `order` here would retitle the dialog
      // "New Email Order" mid close-animation. Every open sets its own target.
      onClose={() => setEmailOrderDialog((d) => ({ ...d, open: false }))}
      memorialRecordId={memorialRecordId}
      order={emailOrderDialog.order}
      onSaved={load}
    />
    </>
  );
}

function OrderSection({ order, onUpdated, apiBase = "admin", readOnly = false, allowEmailOrders = true, onUpdateEmailOrder }) {
  const isEmailOrder = Boolean(order.is_email_order);
  const [status, setStatus] = useState(order.status || "pending");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  // The row re-renders from a refetched order after every save, so the control has to
  // follow the order rather than keep its first value — otherwise it snaps back to the
  // old status and invites the admin to re-submit it.
  useEffect(() => {
    setStatus(order.status || "pending");
  }, [order.status]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/${apiBase}/orders/${order.order_id}/status`, { status, notes: note });
      toast.success(`Order ${order.order_number} updated`);
      setNote("");
      onUpdated();
    } catch (e) {
      toast.error(e.response?.data?.message || "Failed to update status");
    } finally {
      setSaving(false);
    }
  };

  const history = order.status_history || [];

  return (
    <div className="space-y-3">
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="font-semibold text-slate-900">{order.order_number}</p>
          <p className="text-xs text-slate-500">
            {fmt(order.created_at)} · {money(order.total_charged || order.total)} · Placed by {order.placed_by === "funeral_home" ? "Funeral Home" : "Family"}
          </p>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-1.5">
          {order.is_email_order && (
            <Badge variant="outline" className="border-amber-200 bg-amber-50 text-amber-700">Email Order</Badge>
          )}
          <PaymentStatusBadge status={order.payment_status} />
          <OrderStageBadge order={order} />
        </div>
      </div>

      {/* Buyer + shipping address, per order (TR-23 #2). A case can carry orders from
          several different family members, so this must never be read off the case:
          each order stores the details of whoever actually paid for it. Without this
          block an admin has no address to ship to. */}
      <div className="grid sm:grid-cols-2 gap-3 text-sm bg-slate-50 rounded-lg p-3">
        <div>
          <p className="text-[10px] uppercase font-bold text-slate-400 mb-0.5">Ordered by</p>
          <p className="text-slate-800">{order.customer_name || "—"}</p>
          {order.customer_email || order.contact_email ? (
            <p className="text-xs text-slate-500 break-all">{order.customer_email || order.contact_email}</p>
          ) : null}
          {order.shipping_address?.phone ? <p className="text-xs text-slate-500">{order.shipping_address.phone}</p> : null}
        </div>
        <div>
          <p className="text-[10px] uppercase font-bold text-slate-400 mb-0.5">Ship to</p>
          {order.shipping_address ? (
            <>
              <p className="text-slate-800">{order.shipping_address.name || order.customer_name || "—"}</p>
              <p className="text-xs text-slate-500">{addrLine(order.shipping_address) || "—"}</p>
            </>
          ) : (
            <p className="text-xs text-slate-400">No address on this order</p>
          )}
          {order.billing_address && addrLine(order.billing_address) !== addrLine(order.shipping_address) ? (
            <p className="text-xs text-slate-400 mt-1">Billing: {addrLine(order.billing_address)}</p>
          ) : null}
        </div>
      </div>

      {/* items with product images */}
      <div className="space-y-2">
        {(order.items || []).map((it, i) => {
          const img = it.image_url ? (it.image_url.startsWith("http") ? it.image_url : getImageUrl(it.image_url)) : null;
          return (
            <div key={i} className="flex items-center gap-3 text-sm">
              <div className="w-12 h-12 rounded-lg border bg-slate-50 flex items-center justify-center overflow-hidden shrink-0">
                {img ? <img src={img} alt={it.name} className="w-full h-full object-contain" /> : <span className="text-[9px] text-slate-300">no image</span>}
              </div>
              <div className="min-w-0">
                <p className="text-slate-800 font-medium">{it.name} × {it.quantity} — {money(it.display_price)}</p>
                {(it.engraving_lines || []).length > 0 && <p className="text-xs text-slate-400">Engraving: {it.engraving_lines.join(" / ")}</p>}
                {it.ash_chamber && <p className="text-xs text-slate-400">+ Ash chamber</p>}
                {/* The print chosen for this line, when the buyer picked one. Opens the
                    full-size image in a new tab, same as the Case Images section above. */}
                {it.custom_image_url ? (
                  <a
                    href={getImageUrl(it.custom_image_url)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-1 inline-flex items-center gap-1.5 text-xs text-blue-700 hover:underline"
                  >
                    <img src={getImageUrl(it.custom_image_url)} alt="Selected print" className="w-10 h-10 object-cover rounded border" />
                    {/* Deliberately generic: the same link carries paw, nose and
                        signature prints too, not only fingerprints (TR-22). */}
                    <span>View Images ↗</span>
                  </a>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>

      {history.length > 0 && (
        <div className="bg-slate-50 rounded-lg p-2 space-y-1">
          {history.map((h, i) => (
            <div key={i} className="text-[11px] text-slate-500 flex items-start gap-1">
              <Clock className="w-3 h-3 mt-0.5" /> <span className="font-medium capitalize">{h.status}</span>
              {h.note ? ` — ${h.note}` : ""} <span className="text-slate-400">· {fmt(h.at)} ({h.changed_by})</span>
            </div>
          ))}
        </div>
      )}

      {/* Status change — admin only; hidden in read-only (FH) mode.
          An email order gets its own button instead of the inline row: it moves through
          two rungs only, and the popup is where its status and remark are chosen. Each
          card carries its own button, scoped to that order — the case-level green button
          now only ever opens a NEW email order, so recording a second phoned-in order no
          longer overwrites the first. */}
      {!readOnly && (isEmailOrder ? (allowEmailOrders && (
        <div className="print:hidden flex justify-end border-t pt-3">
          <Button size="sm" variant="outline" onClick={() => onUpdateEmailOrder?.(order)}>
            <Mail className="w-4 h-4 mr-1.5" /> Update Status
          </Button>
        </div>
      )) : (
        <div className="print:hidden flex flex-wrap items-end gap-2 border-t pt-3">
          <div className="w-40">
            <label className="text-[10px] text-slate-400 uppercase font-bold">Status</label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {STATUS_OPTS.map((x) => <SelectItem key={x} value={x} className="capitalize">{x}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Textarea placeholder="Note (optional)" value={note} onChange={(e) => setNote(e.target.value)} className="flex-1 min-h-[40px] h-10" />
          <Button size="sm" onClick={save} disabled={saving}>{saving ? "Saving…" : "Update"}</Button>
        </div>
      ))}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="bg-slate-50/60 border rounded-xl p-4">
      <h4 className="font-semibold text-slate-800 mb-2">{title}</h4>
      {children}
    </div>
  );
}
function KV({ k, v }) {
  return (
    <div className="flex justify-between gap-3 border-b border-slate-100 py-0.5">
      <span className="text-slate-500">{k}</span><span className="font-medium text-slate-800 text-right">{v}</span>
    </div>
  );
}
